import { inject, onMounted, onUnmounted } from "vue"
import { startDrag, activeDrags, cleanupDrag } from "./dragEngine"
import { dragState } from "./dragState"

export function useMassDraggable(containerRef, getItemFromEl) {
    // const dragState = inject("dragState")
    let destroyed = false
    const DRAG_THRESHOLD = 2
    
    const onMouseDown = e => {
        if (destroyed) return
        const el = e.target.closest('.draggable')
        if (!el) return
        const item = getItemFromEl(el)
        if (!item) return
        
        const startX = e.clientX
        const startY = e.clientY
        let dragStarted = false
        let stop = null
        
        const onMove = moveEvent => {
            const dx = moveEvent.clientX - startX
            const dy = moveEvent.clientY - startY
            if (!dragStarted && Math.sqrt(dx * dx + dy * dy) > DRAG_THRESHOLD) {
                dragStarted = true
                e.preventDefault()
                stop = startDrag(item, el, moveEvent, dragState)
            }
        }
        
        const onDragStart = e => e.preventDefault()
        el.addEventListener("dragstart", onDragStart)
        
        const onUp = () => {
            dragState.dropConsumed = false
            window.removeEventListener("mousemove", onMove)
            window.removeEventListener("mouseup", onUp)
            el.removeEventListener("dragstart", onDragStart)
        }
        
        window.addEventListener("mousemove", onMove)
        window.addEventListener("mouseup", onUp, { once: true })
    }

    const onTouchStart = e => {
        if (destroyed) return
        const el = e.target.closest('.draggable')
        if (!el) return
        const touch = e.touches?.[0]
        if (!touch) return
        const item = getItemFromEl(el)
        if (!item) return
        
        const startX = touch.clientX
        const startY = touch.clientY
        let dragStarted = false
        
        const onMove = moveEvent => {
            const t = moveEvent.touches?.[0]
            if (!t) return
            const dx = t.clientX - startX
            const dy = t.clientY - startY
            if (!dragStarted && Math.sqrt(dx * dx + dy * dy) > DRAG_THRESHOLD) {
                dragStarted = true
                moveEvent.preventDefault()
                startDrag(item, el, {
                    clientX: t.clientX,
                    clientY: t.clientY,
                    preventDefault: () => moveEvent.preventDefault()
                }, dragState)
            }
        }
        
        const onEnd = () => {
            el.removeEventListener("touchmove", onMove)
            el.removeEventListener("touchend", onEnd)
            el.removeEventListener("touchcancel", onEnd)
        }
        
        el.addEventListener("touchmove", onMove, { passive: false })
        el.addEventListener("touchend", onEnd, { once: true })
        el.addEventListener("touchcancel", onEnd, { once: true })
    }
        
    onMounted(() => {
        const el = containerRef.value?.$el ?? containerRef.value
        if (!el) return
        el.addEventListener("mousedown", onMouseDown)
        el.addEventListener("touchstart", onTouchStart, { passive: false })
        window.addEventListener('keydown', onKeyDown)
        window.addEventListener('keyup', onKeyUp)
    })

    onUnmounted(() => {
        destroyed = true
        const el = containerRef.value?.$el ?? containerRef.value
        if (el) {
            el.removeEventListener("mousedown", onMouseDown)
            el.removeEventListener("touchstart", onTouchStart)
        }
        window.removeEventListener('keydown', onKeyDown)
        window.removeEventListener('keyup', onKeyUp)
        if (dragState.clone) {
            dragState.clone.remove()
            dragState.clone = null
            dragState.item = null
            dragState.dropConsumed = false
        }
    })
    
    function onKeyDown(e) {
        if (e.key === 'Control') {
            dragState.copy = true
            dragState.clone?.classList.add('copy')
        }
    }
    function onKeyUp(e) {
        if (e.key === 'Control') {
            dragState.copy = false
            dragState.clone?.classList.remove('copy')
        }
    }
    
    return { dragState }
}